import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShoppingBagIcon, MinusIcon, PlusIcon, TrashIcon } from './icons';
import type { CartItem } from '../types/app';

interface CartOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  total: string;
  isLoading: boolean;
  onUpdateQuantity: (basketProductId: number, variantId: number, quantity: number) => void;
  onRemoveItem: (basketProductId: number) => void;
  onProductClick?: (productId: number, variantId: number) => void;
}

function CartItemRow({
  item,
  onUpdateQuantity,
  onRemoveItem,
  onProductClick,
}: {
  item: CartItem;
  onUpdateQuantity: (basketProductId: number, variantId: number, quantity: number) => void;
  onRemoveItem: (basketProductId: number) => void;
  onProductClick?: (productId: number, variantId: number) => void;
}) {
  const imageUrl = item.productVariant.image_url || item.product.image_url;
  const hasDiscount = item.discountVal > 0;
  const variantPrice = item.productVariant.price;

  return (
    <div className="flex items-center gap-4 py-4 border-b border-brand-gold/10 last:border-b-0">
      {/* Product thumbnail — clickable to open PDP */}
      <div
        className={`w-12 h-12 rounded-sm overflow-hidden flex-shrink-0 bg-brand-navy/5${onProductClick ? ' cursor-pointer' : ''}`}
        onClick={onProductClick ? () => onProductClick(item.product.id, item.productVariant.id) : undefined}
        role={onProductClick ? 'button' : undefined}
        tabIndex={onProductClick ? 0 : undefined}
        onKeyDown={onProductClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onProductClick(item.product.id, item.productVariant.id); } : undefined}
        aria-label={onProductClick ? `View details for ${item.product.title}` : undefined}
      >
        <img
          src={imageUrl}
          alt={item.product.title}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Product info */}
      <div className="flex-1 min-w-0">
        {/* Product title — clickable to open PDP */}
        <p
          className={`font-serif text-sm text-brand-navy truncate${onProductClick ? ' cursor-pointer hover:text-brand-gold transition-colors' : ''}`}
          onClick={onProductClick ? () => onProductClick(item.product.id, item.productVariant.id) : undefined}
          role={onProductClick ? 'button' : undefined}
          tabIndex={onProductClick ? 0 : undefined}
          onKeyDown={onProductClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onProductClick(item.product.id, item.productVariant.id); } : undefined}
        >
          {item.product.title}
        </p>
        <p className="text-xs text-brand-navy/60 truncate">
          {item.productVariant.title}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {hasDiscount ? (
            <>
              <span className="text-xs text-brand-navy/40 line-through">
                {variantPrice}
              </span>
              <span className="text-xs text-brand-navy font-medium">
                {/* Show discounted price: parse and subtract discount */}
                {variantPrice}
              </span>
            </>
          ) : (
            <span className="text-xs text-brand-navy font-medium">
              {variantPrice}
            </span>
          )}
        </div>
      </div>

      {/* Quantity controls */}
      <div className="flex items-center border border-brand-gold/20 rounded-sm">
        <button
          onClick={() =>
            onUpdateQuantity(item.id, item.productVariant.id, item.quantity - 1)
          }
          className="p-1.5 text-brand-navy/60 hover:text-brand-navy transition-colors"
          aria-label="Decrease quantity"
        >
          <MinusIcon size={14} strokeWidth={1.5} />
        </button>
        <span className="px-2 text-xs text-brand-navy font-medium min-w-[20px] text-center">
          {item.quantity}
        </span>
        <button
          onClick={() =>
            onUpdateQuantity(item.id, item.productVariant.id, item.quantity + 1)
          }
          className="p-1.5 text-brand-navy/60 hover:text-brand-navy transition-colors"
          aria-label="Increase quantity"
        >
          <PlusIcon size={14} strokeWidth={1.5} />
        </button>
      </div>

      {/* Remove button */}
      <button
        onClick={() => onRemoveItem(item.id)}
        className="p-1.5 text-brand-navy/40 hover:text-red-500 transition-colors"
        aria-label="Remove item"
      >
        <TrashIcon size={16} strokeWidth={1.5} />
      </button>
    </div>
  );
}

export function CartOverlay({
  isOpen,
  onClose,
  items,
  total,
  isLoading,
  onUpdateQuantity,
  onRemoveItem,
  onProductClick,
}: CartOverlayProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-10 bg-brand-cream/[0.98] backdrop-blur-sm flex flex-col shadow-2xl border border-brand-gold/20 z-20"
        >
          {/* Header */}
          <div className="px-8 py-6 border-b border-brand-gold/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="font-serif text-brand-navy tracking-wide text-lg">
                  Your Bag
                </h2>
                {items.length > 0 && (
                  <span className="text-xs text-brand-navy/60 bg-brand-navy/5 px-2 py-0.5 rounded-full">
                    {items.length} {items.length === 1 ? 'item' : 'items'}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1 text-brand-navy/40 hover:text-brand-navy transition-colors"
                aria-label="Close bag"
              >
                <XIcon size={20} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-4">
            {items.length === 0 ? (
              isLoading ? null : (
                /* Empty state — only after initial load resolves */
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <ShoppingBagIcon
                    size={48}
                    className="text-brand-navy/20"
                    strokeWidth={1}
                  />
                  <p className="font-serif text-brand-navy/60 text-sm">
                    Your bag is empty
                  </p>
                  <p className="text-xs text-brand-navy/40">
                    Items you reserve will appear here
                  </p>
                </div>
              )
            ) : (
              /* Items list */
              <div>
                {items.map((item) => (
                  <CartItemRow
                    key={item.id}
                    item={item}
                    onUpdateQuantity={onUpdateQuantity}
                    onRemoveItem={onRemoveItem}
                    onProductClick={onProductClick}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer: Total */}
          {!isLoading && items.length > 0 && (
            <div className="px-8 py-6 border-t border-brand-gold/20">
              <div className="flex items-center justify-between">
                <span className="font-serif text-brand-navy tracking-wide">
                  Total
                </span>
                <span className="font-serif text-brand-navy text-lg font-medium">
                  {total}
                </span>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
